import { Component, Input, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatSliderChange } from '@angular/material/slider';
import { Subscription, pairwise, startWith } from 'rxjs';
import { NotOnlySpaces } from '../../../shared/CustomValidators/CustomNotOnlySpacesValidator';
import { EmailValidator } from '../../../shared/CustomValidators/CustomInvalidEmailValidator';
import { SubofficeService } from 'src/app/modules/setting/company-settings/suboffice/suboffice.service';
import { first } from 'rxjs';

@Component({
    selector: 'app-staff-info-form',
    templateUrl: './staff-info-form.component.html',
    styleUrls: ['./staff-info-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StaffInfoFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => StaffInfoFormComponent),
            multi: true
        }
    ],
    standalone: false
})
export class StaffInfoFormComponent implements OnInit {
  public CreditLimit: boolean = false;
  public staffInfoFormSubscription: Subscription | undefined;
  public isFormTouched = false;
  @Input() form: any = {};
  @Input() model: any = {};
  @Input() isEdit : boolean = false;
  public passwordType: string = 'password';

  public staffInfoForm: UntypedFormGroup = new UntypedFormGroup({
    hr_code: new UntypedFormControl(null,[]),
    first_name: new UntypedFormControl(null,[Validators.required,NotOnlySpaces()]),
    last_name: new UntypedFormControl(null,[Validators.required,NotOnlySpaces()]),
    email: new UntypedFormControl(null,[Validators.required ,EmailValidator()]),
    password: new UntypedFormControl(null,[Validators.required, Validators.minLength(12)]),
    can_login:new UntypedFormControl(true),
    attach_tenents: new UntypedFormControl(null, [Validators.required]),
    current_tenent: new UntypedFormControl(null, [Validators.required]),
  });

  activeSubOffices : any = [];
  sub_offices: any = [];
  filteredSubOffices: any[] = [];

  constructor(public subofficeService: SubofficeService) { }


  ngOnChanges(changes: SimpleChanges) {
    this.model = changes?.model?.currentValue;
  }


  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngAfterViewInit(): void {
    this.setDataOnEdit();
    this.getActiveSubOffices();
    this.setupFormListeners();
  }


  setupFormListeners() {
    this.filteredSubOffices = this?.model?.attach_tenents;
    this.staffInfoForm.get('attach_tenents')?.valueChanges.subscribe((selectedSubOffices: any[]) => {
      const selected = selectedSubOffices || [];
      this.filteredSubOffices = selected;

      // Keep the current default if it's still one of the selected offices; otherwise
      // promote the next remaining office (first in the list). It doesn't matter whether
      // that next office is a parent company or a sub-office — just the next one left.
      // Nothing selected → no default.
      const currentDefault = this.staffInfoForm.get('current_tenent')?.value;
      const stillSelected = selected.find((o: any) => this.isSameOffice(o, currentDefault));
      this.staffInfoForm
        .get('current_tenent')
        ?.setValue(selected.length ? (stillSelected ?? selected[0]) : null);
    });
  }

  /** Offices are compared by id — the identifier that's sent to the backend. */
  isSameOffice = (a: any, b: any): boolean => !!a && !!b && a.id === b.id;


  setDataOnEdit = () => {
    if(this.isEdit) {
      this.staffInfoForm.get('password')?.clearValidators();
      this.staffInfoForm.get('password')?.setValidators([]);
      this.staffInfoForm.get('password')?.updateValueAndValidity();

      this.staffInfoForm.get('hr_code')?.setValue(this?.model?.staff_detail?.hr_code);
      this.staffInfoForm.get('first_name')?.setValue(this?.model?.first_name);
      this.staffInfoForm.get('last_name')?.setValue(this?.model?.last_name);
      this.staffInfoForm.get('email')?.setValue(this?.model?.email);
      this.staffInfoForm.get('password')?.setValue(this?.model?.password);
      this.staffInfoForm.get('attach_tenents')?.setValue(this?.model?.attach_tenents);
      this.staffInfoForm.get('current_tenent')?.setValue(this?.model?.current_tenent);
    }
  }

  onChangeCreditLimit() {
    this.CreditLimit = !this.CreditLimit;
  }
  formatLabel(value: number) {
    if (value >= 1000) {
      return Math.round(value / 1000) + 'k';
    }

    return value;
  }



  writeValue(val: any): void {
    val && this.staffInfoForm.setValue(val, { emitEvent: false });
  }

  registerOnChange(fn: any): void {

    this.staffInfoForm.valueChanges.subscribe(fn);
  }


  public onTouched: () => void = () => {};

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.staffInfoForm.disable() : this.staffInfoForm.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    console.log('validate this.staffInfoForm', this.staffInfoForm);
    return this.staffInfoForm.valid
        ? null
        : { invalidForm: { valid: false, message: "Fields are invalid" } };
  }

  public showPassword() {
    this.passwordType = ( (this.passwordType == 'password') ? 'text' : 'password');
  }


// generate Strong Password
  generateRandomPassword(length: number): string {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+[]{}|;:,.<>?';
    let password = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset.charAt(randomIndex);
    }
    return password;
  }

  generateAndSetPassword() {
    const newPassword = this.generateRandomPassword(12);
    this.staffInfoForm.patchValue({
      password: newPassword
    });
  }


  initSubscriptions(){
        if(this.form){
      this.staffInfoFormSubscription = this.form.valueChanges
        .pipe(startWith(null), pairwise())
        .subscribe(([prev, next]: [any, any]) => {
          if (this.form.touched && !this.isFormTouched){
            this.isFormTouched = true;
            this.touchFormControls();
          }
        });
    }
  }

  touchFormControls(){
    Object.keys(this.staffInfoForm.controls).forEach(key => {
      this.staffInfoForm.get(key)?.markAsTouched();
    });
  }

  onInputChange(event: MatSliderChange) {
    console.log(event.value);
  }

  getActiveSubOffices() {
    this.subofficeService.getSubOffices({status: 1})
      .pipe(first())
      .subscribe((response) => {
        this.activeSubOffices = response?.data;
      });
  }
}
